import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Typography, 
  Divider, 
  Switch, 
  Modal, 
  Alert, 
  Space,
  Tabs,
  message,
  Row,
  Col
} from 'antd'
import { 
  UserOutlined, 
  LockOutlined, 
  SafetyOutlined,
  QrcodeOutlined,
  KeyOutlined,
  CopyOutlined,
  ReloadOutlined
} from '@ant-design/icons'
import { QRCodeSVG } from 'qrcode.react'
import { useAuth } from '../../contexts/AuthContext'
import { authApi } from '../../services/api'

const { Title, Text } = Typography
const { TabPane } = Tabs

interface Setup2FAData {
  secret: string
  qrCode: string
  manualEntryKey: string
}

const AdminProfile: React.FC = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [setup2FAVisible, setSetup2FAVisible] = useState(false)
  const [setup2FAData, setSetup2FAData] = useState<Setup2FAData | null>(null)
  const [backupCodesVisible, setBackupCodesVisible] = useState(false)
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [disable2FAVisible, setDisable2FAVisible] = useState(false)
  
  const [profileForm] = Form.useForm()
  const [passwordForm] = Form.useForm()
  const [setup2FAForm] = Form.useForm()
  const [disable2FAForm] = Form.useForm()
  const [regenerateCodesForm] = Form.useForm()

  useEffect(() => {
    if (user) {
      profileForm.setFieldsValue({
        name: user.name,
        email: user.email
      })
    }
  }, [user, profileForm])

  const handleUpdateProfile = async (values: { name: string }) => {
    try {
      setLoading(true)
      // Profile update API would go here
      console.log('Updating profile with:', values)
      message.success('Profile updated successfully')
    } catch (error) {
      console.error('Profile update error:', error)
      message.error('Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  const handleChangePassword = async (values: { currentPassword: string; newPassword: string }) => {
    try {
      setLoading(true)
      await authApi.changePassword(values.currentPassword, values.newPassword)
      passwordForm.resetFields()
      message.success('Password changed successfully')
    } catch (error: any) {
      console.error('Password change error:', error)
      message.error(error?.response?.data?.message || 'Failed to change password')
    } finally {
      setLoading(false)
    }
  }

  const handleSetup2FA = async () => {
    try {
      setLoading(true)
      const data = await authApi.setup2FA()
      setSetup2FAData(data)
      setSetup2FAVisible(true)
    } catch (error: any) {
      console.error('2FA setup error:', error)
      message.error(error?.response?.data?.message || 'Failed to setup 2FA')
    } finally {
      setLoading(false)
    }
  }

  const handleEnable2FA = async (values: { token: string }) => {
    if (!setup2FAData) return
    
    try {
      setLoading(true)
      const response = await authApi.enable2FA(setup2FAData.secret, values.token)
      setBackupCodes(response.backupCodes)
      setSetup2FAVisible(false)
      setBackupCodesVisible(true)
      setup2FAForm.resetFields()
      message.success('2FA enabled successfully')
      // Refresh user data
      window.location.reload()
    } catch (error: any) {
      console.error('2FA enable error:', error)
      message.error(error?.response?.data?.message || 'Failed to enable 2FA')
    } finally {
      setLoading(false)
    }
  }

  const handleDisable2FA = async (values: { currentPassword: string; twoFactorToken?: string; backupCode?: string }) => {
    try {
      setLoading(true)
      await authApi.disable2FA(values.currentPassword, values.twoFactorToken, values.backupCode)
      setDisable2FAVisible(false)
      disable2FAForm.resetFields()
      message.success('2FA disabled successfully')
      // Refresh user data
      window.location.reload()
    } catch (error: any) {
      console.error('2FA disable error:', error)
      message.error(error?.response?.data?.message || 'Failed to disable 2FA')
    } finally {
      setLoading(false)
    }
  }

  const handleRegenerateBackupCodes = async (values: { currentPassword: string; twoFactorToken?: string; backupCode?: string }) => {
    try {
      setLoading(true)
      const response = await authApi.regenerateBackupCodes(values.currentPassword, values.twoFactorToken, values.backupCode)
      setBackupCodes(response.backupCodes)
      setBackupCodesVisible(true)
      regenerateCodesForm.resetFields()
      message.success('Backup codes regenerated successfully')
    } catch (error: any) {
      console.error('Regenerate backup codes error:', error)
      message.error(error?.response?.data?.message || 'Failed to regenerate backup codes')
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    message.success('Copied to clipboard')
  }

  const copyAllBackupCodes = () => {
    const allCodes = backupCodes.join('\n')
    navigator.clipboard.writeText(allCodes)
    message.success('All backup codes copied to clipboard')
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Title level={2} className="mb-6">Account Settings</Title>
      
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          {/* Profile Information */}
          <Card title="Profile Information" className="mb-6">
            <Form
              form={profileForm}
              layout="vertical"
              onFinish={handleUpdateProfile}
            >
              <Form.Item
                name="name"
                label="Full Name"
                rules={[{ required: true, message: 'Please enter your name' }]}
              >
                <Input prefix={<UserOutlined />} />
              </Form.Item>
              
              <Form.Item name="email" label="Email Address">
                <Input prefix={<UserOutlined />} disabled />
              </Form.Item>
              
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Update Profile
                </Button>
              </Form.Item>
            </Form>
          </Card>

          {/* Change Password */}
          <Card title="Change Password">
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handleChangePassword}
            >
              <Form.Item
                name="currentPassword"
                label="Current Password"
                rules={[{ required: true, message: 'Please enter your current password' }]}
              >
                <Input.Password prefix={<LockOutlined />} />
              </Form.Item>
              
              <Form.Item
                name="newPassword"
                label="New Password"
                rules={[
                  { required: true, message: 'Please enter your new password' },
                  { min: 6, message: 'Password must be at least 6 characters' }
                ]}
              >
                <Input.Password prefix={<LockOutlined />} />
              </Form.Item>
              
              <Form.Item
                name="confirmPassword"
                label="Confirm New Password"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Please confirm your new password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve()
                      }
                      return Promise.reject(new Error('Passwords do not match'))
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} />
              </Form.Item>
              
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Change Password
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          {/* Two-Factor Authentication */}
          <Card title="Two-Factor Authentication" className="mb-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Title level={5} className="mb-1">
                    <SafetyOutlined className="mr-2" />
                    2FA Status
                  </Title>
                  <Text type="secondary">
                    {user?.twoFactorEnabled 
                      ? 'Two-factor authentication is enabled and protecting your account' 
                      : 'Add an extra layer of security to your account'
                    }
                  </Text>
                </div>
                <Switch 
                  checked={user?.twoFactorEnabled || false} 
                  disabled
                />
              </div>

              <Divider />

              {!user?.twoFactorEnabled ? (
                <div>
                  <Alert
                    message="Secure your account"
                    description="Enable two-factor authentication to add an extra layer of security to your account."
                    type="info"
                    showIcon
                    className="mb-4"
                  />
                  <Button 
                    type="primary" 
                    icon={<SafetyOutlined />}
                    onClick={handleSetup2FA}
                    loading={loading}
                  >
                    Enable 2FA
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Text type="secondary">
                    Enabled on: {user.twoFactorSetupAt ? new Date(user.twoFactorSetupAt).toLocaleDateString() : 'Unknown'}
                  </Text>
                  
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Button 
                      icon={<ReloadOutlined />}
                      onClick={() => {
                        Modal.confirm({
                          title: 'Regenerate Backup Codes',
                          content: (
                            <Form
                              form={regenerateCodesForm}
                              layout="vertical"
                              onFinish={handleRegenerateBackupCodes}
                            >
                              <Alert
                                message="This will invalidate all your current backup codes"
                                type="warning"
                                className="mb-4"
                              />
                              <Form.Item
                                name="currentPassword"
                                label="Current Password"
                                rules={[{ required: true, message: 'Please enter your current password' }]}
                              >
                                <Input.Password />
                              </Form.Item>
                              <Tabs defaultActiveKey="token">
                                <TabPane tab="Authenticator Code" key="token">
                                  <Form.Item
                                    name="twoFactorToken"
                                    label="6-digit code"
                                    rules={[
                                      { required: true, message: 'Please enter the 6-digit code' },
                                      { pattern: /^\d{6}$/, message: 'Code must be exactly 6 digits' }
                                    ]}
                                  >
                                    <Input placeholder="000000" maxLength={6} />
                                  </Form.Item>
                                </TabPane>
                                <TabPane tab="Backup Code" key="backup">
                                  <Form.Item
                                    name="backupCode"
                                    label="8-character backup code"
                                    rules={[
                                      { required: true, message: 'Please enter the backup code' },
                                      { pattern: /^[0-9A-F]{8}$/i, message: 'Invalid backup code format' }
                                    ]}
                                  >
                                    <Input placeholder="XXXXXXXX" maxLength={8} />
                                  </Form.Item>
                                </TabPane>
                              </Tabs>
                            </Form>
                          ),
                          onOk: () => regenerateCodesForm.submit(),
                          okText: 'Regenerate',
                          okButtonProps: { loading }
                        })
                      }}
                    >
                      Regenerate Backup Codes
                    </Button>
                    
                    <Button 
                      danger
                      onClick={() => setDisable2FAVisible(true)}
                    >
                      Disable 2FA
                    </Button>
                  </Space>
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Setup 2FA Modal */}
      <Modal
        title="Setup Two-Factor Authentication"
        open={setup2FAVisible}
        onCancel={() => {
          setSetup2FAVisible(false)
          setSetup2FAData(null)
          setup2FAForm.resetFields()
        }}
        footer={null}
        width={600}
      >
        {setup2FAData && (
          <div>
            <Alert
              message="Scan the QR code with your authenticator app"
              description="Use apps like Google Authenticator, Authy, or 1Password to scan this QR code"
              type="info"
              className="mb-4"
            />
            
            <Tabs defaultActiveKey="qr">
              <TabPane tab={<span><QrcodeOutlined />QR Code</span>} key="qr">
                <div className="text-center">
                  <QRCodeSVG value={setup2FAData.qrCode} size={200} />
                </div>
              </TabPane>
              <TabPane tab={<span><KeyOutlined />Manual Entry</span>} key="manual">
                <div>
                  <Text strong>Manual entry key:</Text>
                  <div className="bg-gray-100 p-3 rounded mt-2 flex items-center justify-between">
                    <Text code className="text-sm">{setup2FAData.manualEntryKey}</Text>
                    <Button 
                      icon={<CopyOutlined />} 
                      size="small"
                      onClick={() => copyToClipboard(setup2FAData.manualEntryKey)}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              </TabPane>
            </Tabs>

            <Divider />

            <Form
              form={setup2FAForm}
              layout="vertical"
              onFinish={handleEnable2FA}
            >
              <Form.Item
                name="token"
                label="Enter the 6-digit code from your authenticator app"
                rules={[
                  { required: true, message: 'Please enter the 6-digit code' },
                  { pattern: /^\d{6}$/, message: 'Code must be exactly 6 digits' }
                ]}
              >
                <Input 
                  placeholder="000000" 
                  maxLength={6}
                  style={{ textAlign: 'center', fontSize: '18px', letterSpacing: '0.3em' }}
                />
              </Form.Item>
              
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Enable 2FA
                  </Button>
                  <Button onClick={() => {
                    setSetup2FAVisible(false)
                    setSetup2FAData(null)
                    setup2FAForm.resetFields()
                  }}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* Backup Codes Modal */}
      <Modal
        title="Your Backup Codes"
        open={backupCodesVisible}
        onCancel={() => setBackupCodesVisible(false)}
        footer={[
          <Button key="copy" icon={<CopyOutlined />} onClick={copyAllBackupCodes}>
            Copy All
          </Button>,
          <Button key="close" type="primary" onClick={() => setBackupCodesVisible(false)}>
            I've Saved These Codes
          </Button>
        ]}
        width={500}
      >
        <Alert
          message="Save these backup codes in a safe place"
          description="Each code can only be used once. Use them to sign in if you lose access to your authenticator device."
          type="warning"
          className="mb-4"
        />
        
        <div className="grid grid-cols-2 gap-2">
          {backupCodes.map((code, index) => (
            <div key={index} className="bg-gray-100 p-2 rounded text-center font-mono">
              {code}
            </div>
          ))}
        </div>
      </Modal>

      {/* Disable 2FA Modal */}
      <Modal
        title="Disable Two-Factor Authentication"
        open={disable2FAVisible}
        onCancel={() => {
          setDisable2FAVisible(false)
          disable2FAForm.resetFields()
        }}
        footer={null}
        width={500}
      >
        <Alert
          message="This will make your account less secure"
          description="Disabling 2FA will remove the extra layer of protection from your account."
          type="error"
          className="mb-4"
        />
        
        <Form
          form={disable2FAForm}
          layout="vertical"
          onFinish={handleDisable2FA}
        >
          <Form.Item
            name="currentPassword"
            label="Current Password"
            rules={[{ required: true, message: 'Please enter your current password' }]}
          >
            <Input.Password />
          </Form.Item>
          
          <Tabs defaultActiveKey="token">
            <TabPane tab="Authenticator Code" key="token">
              <Form.Item
                name="twoFactorToken"
                label="6-digit code"
                rules={[
                  { required: true, message: 'Please enter the 6-digit code' },
                  { pattern: /^\d{6}$/, message: 'Code must be exactly 6 digits' }
                ]}
              >
                <Input placeholder="000000" maxLength={6} />
              </Form.Item>
            </TabPane>
            <TabPane tab="Backup Code" key="backup">
              <Form.Item
                name="backupCode"
                label="8-character backup code"
                rules={[
                  { required: true, message: 'Please enter the backup code' },
                  { pattern: /^[0-9A-F]{8}$/i, message: 'Invalid backup code format' }
                ]}
              >
                <Input placeholder="XXXXXXXX" maxLength={8} />
              </Form.Item>
            </TabPane>
          </Tabs>
          
          <Form.Item>
            <Space>
              <Button type="primary" danger htmlType="submit" loading={loading}>
                Disable 2FA
              </Button>
              <Button onClick={() => {
                setDisable2FAVisible(false)
                disable2FAForm.resetFields()
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default AdminProfile